/**
 * Multi-account storage manager.
 *
 * Provides CRUD operations over a registry of saved user accounts
 * persisted in `localStorage`. Each entry snapshots the user's identity
 * and their last-known refresh token so the session can be resumed
 * without re-authenticating via OTP.
 *
 * The registry lives alongside — not instead of — the existing
 * `accessToken` / `refreshToken` keys. The active session always uses
 * the top-level token keys; the registry is consulted only when
 * switching accounts or rendering the login page's quick-login list.
 *
 * All functions are async to match the {@link storage} abstraction,
 * which allows swapping in an async-native backend (IndexedDB, etc.)
 * without changing call-sites.
 *
 * @module lib/accounts
 */

import { storage } from '@api/lib/storage';
import { tryCatch } from '@learnai/tools/try-catch';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key for the saved-accounts JSON array. */
const ACCOUNTS_KEY = 'savedAccounts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A resumable user session snapshot stored in the accounts registry. */
export interface SavedAccount {
	/** User CUID — used as the unique key for upserts. */
	id: string;
	/** Email address — displayed in the quick-login list. */
	email: string;
	/** Display name (nullable — some users only have an email). */
	name: string | null;
	/** Avatar URL (nullable). */
	avatarUrl: string | null;
	/** Snapshot of the refresh token from when this account was last active. */
	refreshToken: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Reads and parses the saved-accounts array from storage.
 *
 * Returns an empty array on any parse failure so callers never need to
 * handle `null`.
 */
async function readAccounts(): Promise<SavedAccount[]> {
	const raw = await storage.local.get(ACCOUNTS_KEY);
	if (!raw) return [];

	const { data, error } = tryCatch(() => JSON.parse(raw) as unknown);
	if (error || !Array.isArray(data)) return [];

	// Defensive — filter out malformed entries that may have been written
	// by a previous version or manually tampered with.
	return data.filter(
		(entry): entry is SavedAccount =>
			typeof entry === 'object' &&
			entry !== null &&
			typeof (entry as SavedAccount).id === 'string' &&
			typeof (entry as SavedAccount).email === 'string' &&
			typeof (entry as SavedAccount).refreshToken === 'string',
	);
}

/** Serialises and persists the accounts array to storage. */
async function writeAccounts(accounts: SavedAccount[]): Promise<void> {
	if (accounts.isEmpty()) {
		await storage.local.delete(ACCOUNTS_KEY);
		return;
	}
	await storage.local.set(ACCOUNTS_KEY, JSON.stringify(accounts));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns all saved accounts from the registry.
 *
 * The returned array is a fresh copy — mutations do not affect storage.
 */
export async function getSavedAccounts(): Promise<SavedAccount[]> {
	return readAccounts();
}

/**
 * Adds or updates a saved account in the registry.
 *
 * - If an account with the same `id` already exists, its fields are
 *   replaced with the new values (upsert semantics).
 * - New accounts are prepended to the list so the most recently used
 *   account appears first.
 */
export async function saveAccount(account: SavedAccount): Promise<void> {
	const accounts = await readAccounts();
	const index = accounts.findIndex((a) => a.id === account.id);

	if (index >= 0) {
		// Update in place — preserve list order.
		accounts[index] = account;
	} else {
		// Prepend — most recently added account first.
		accounts.unshift(account);
	}

	await writeAccounts(accounts);
}

/**
 * Updates only the refresh token for an existing saved account.
 *
 * This is called after a token rotation so the registry stays in sync
 * with the server. If the account is not in the registry, this is a
 * no-op (the user may have removed it).
 */
export async function updateAccountToken(id: string, refreshToken: string): Promise<void> {
	const accounts = await readAccounts();
	const entry = accounts.find((a) => a.id === id);

	if (!entry) return;

	entry.refreshToken = refreshToken;
	await writeAccounts(accounts);
}

/**
 * Removes a saved account from the registry by user ID.
 *
 * No-op if the account is not present.
 */
export async function removeAccount(id: string): Promise<void> {
	const accounts = await readAccounts();
	const filtered = accounts.filter((a) => a.id !== id);

	// Avoid a redundant write if nothing changed.
	if (filtered.length === accounts.length) return;

	await writeAccounts(filtered);
}

/**
 * Removes all saved accounts from the registry.
 *
 * Used as a nuclear option — e.g. "Sign out of all accounts".
 */
export async function clearAllAccounts(): Promise<void> {
	await storage.local.delete(ACCOUNTS_KEY);
}
